import React, { useState, useRef, useEffect } from 'react';
import { logout, AuthUser } from '../utils/auth';
import { sound } from '../utils/sound';

interface UserAvatarProps {
  user: AuthUser;
  onLogout: () => void;
  showBackToMenu?: boolean;
  onBackToMenu?: () => void;
}

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5444';
const ADMIN_URL = `${API_URL}/admin`;

const UserAvatar: React.FC<UserAvatarProps> = ({ user, onLogout, showBackToMenu, onBackToMenu }) => {
  const [showFlyout, setShowFlyout] = useState(false);
  const flyoutRef = useRef<HTMLDivElement>(null);
  const prevShowFlyout = useRef<boolean | null>(null); // Track previous value, null = never set

  // Get display name (prefer name, fallback to email)
  const displayName = user.name || user.email;

  // Get initials for avatar
  const getInitials = () => {
    if (user.name) {
      const nameParts = user.name.split(' ');
      if (nameParts.length >= 2) {
        return `${nameParts[0][0]}${nameParts[nameParts.length - 1][0]}`.toUpperCase();
      }
      return user.name.substring(0, 2).toUpperCase();
    }
    return user.email.substring(0, 2).toUpperCase();
  };

  // Close flyout when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (flyoutRef.current && !flyoutRef.current.contains(event.target as Node)) {
        setShowFlyout(false);
      }
    };

    if (showFlyout) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showFlyout]);

  // Play sounds when flyout opens/closes
  // Only play when value actually CHANGES (not on mount or StrictMode re-runs)
  useEffect(() => {
    const prevValue = prevShowFlyout.current;

    // Only play sound if the value actually changed (not on initial mount)
    if (prevValue !== null && prevValue !== showFlyout) {
      if (showFlyout) {
        sound.clickOpen();
      } else {
        sound.clickClose();
      }
    }

    // Always update the previous value
    prevShowFlyout.current = showFlyout;
  }, [showFlyout]);

  const handleLogout = async () => {
    console.log('[UserAvatar] Logging out...');
    setShowFlyout(false);
    await logout();
    console.log('[UserAvatar] Logged out, calling onLogout callback');
    onLogout();
  };

  const handleAdminConsole = () => {
    window.open(ADMIN_URL, '_blank');
    setShowFlyout(false);
  };

  const handleBackToMenu = () => {
    if (onBackToMenu) {
      setShowFlyout(false);
      onBackToMenu();
    }
  };

  // Check for Admin role (case-insensitive). Can be ADMIN, EDITOR or ANALYST
  const isAdmin = user.roles?.some(role => 
    ['admin', 'editor', 'analyst'].includes(role.toLowerCase())
  ) || false;

  return (
    <div className="user-avatar-container" ref={flyoutRef}>
      <div className="user-info" onClick={() => setShowFlyout(!showFlyout)}>
        <span className="user-name">{displayName}</span>
        <div className="avatar">
          {getInitials()}
        </div>
      </div>

      {showFlyout && (
        <div className="user-flyout">
          <div className="flyout-header">
            <div className="flyout-avatar">{getInitials()}</div>
            <div className="flyout-user-info">
              {user.name && <div className="flyout-name">{user.name}</div>}
              <div className="flyout-email">{user.email}</div>
            </div>
          </div>
          <div className="flyout-divider"></div>
          {showBackToMenu && (
            <>
              <button className="flyout-back-button" onClick={handleBackToMenu}>
                <svg className="back-icon" width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M10 12L6 8L10 4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                Back to Menu
              </button>
              <div className="flyout-divider"></div>
            </>
          )}
          {isAdmin && (
            <>
              <button className="flyout-admin-button" onClick={handleAdminConsole}>
                <img src="/admin.svg" alt="" className="admin-icon" />
                Admin Console
              </button>
              <div className="flyout-divider"></div>
            </>
          )}
          <button className="flyout-logout-button" onClick={handleLogout}>
            <img src="/logout.svg" alt="" className="logout-icon" />
            Logout
          </button>
        </div>
      )}
    </div>
  );
};

export default UserAvatar;
